import React from 'react';
import { LegalSectionBody } from '../components/legal/LegalSectionBody';

export const PRIVACY_SUMMARY_CARDS = [
  {
    icon: '📋',
    title: 'Your Data',
    desc: 'We collect only what we need to run StrapIn — profile, messages, discovery, and safety systems.',
  },
  {
    icon: '✓',
    title: 'Verification Privacy',
    desc: 'Verification selfies and videos are never public. Only authorized staff can review them.',
  },
  {
    icon: '📍',
    title: 'Location Privacy',
    desc: 'Discovery uses approximate distance — not live GPS sharing with other users.',
  },
  {
    icon: '💬',
    title: 'Chats & Media',
    desc: 'Private conversations stay in-app. We actively discourage screenshots — with realistic limits.',
  },
  {
    icon: '🗑️',
    title: 'Deletion Rights',
    desc: 'You can delete your account. We explain what is removed and what we may retain for safety.',
  },
  {
    icon: '🔐',
    title: 'Account Security',
    desc: 'Limited network and device evidence helps us investigate scams, fraud, and serious safety reports.',
  },
];

function section(title, icon, bodyProps) {
  return {
    title,
    icon,
    content: <LegalSectionBody {...bodyProps} />,
  };
}

export const PRIVACY_SECTIONS = [
  section('What We Collect', '📋', {
    intro:
      'We collect information to help you connect safely — not to sell your personal data. Here is what that includes:',
    subsections: [
      {
        heading: 'Account & profile',
        bullets: [
          'Email address and/or phone number (if used for sign-in)',
          'Username, bio, and profile photos you choose to upload',
          'Gender identity, sexuality, and preferences you choose to share',
          'App settings and visibility choices',
        ],
      },
      {
        heading: 'Discovery & device',
        bullets: [
          'Location information, including precise coordinates where you permit device location access, used for nearby discovery; other users are shown approximate distance rather than your exact coordinates',
          'Device type, operating system, app version, language, and device or push-notification identifiers used for account security, device management, and notifications',
          'IP address and general network information for security and fraud prevention',
        ],
      },
      {
        heading: 'Messages & interactions',
        bullets: [
          'Chats, pokes, and in-app interactions',
          'Private albums and media you send or receive',
          'Reports, blocks, and safety-related actions you take',
        ],
      },
      {
        heading: 'Verification data',
        bullets: [
          'Selfie, angled selfie, and short liveness video submitted for review',
          'Verification status and review outcomes',
        ],
      },
      {
        heading: 'Safety & moderation',
        bullets: [
          'Report content and categories you select',
          'Moderation notes, fraud flags, and enforcement actions',
          'Investigation data related to scams, harassment, impersonation, or outing threats',
          'Timestamped account-security events such as registration, login, messaging, reports, and Safe Meet actions',
          'Limited network and device signals, including IP address and pseudonymous device/session fingerprints, used to detect linked abusive or fraudulent accounts',
        ],
      },
    ],
  }),
  section('Why We Collect It', '🎯', {
    intro: 'Every category of data serves a clear purpose:',
    bullets: [
      'Creating and securing your account',
      'Showing you nearby people and enabling discovery you control',
      'Delivering messages, albums, and media features',
      'Running verification and anti-impersonation systems',
      'Investigating reports and protecting the community',
      'Preventing fraud, spam, and abuse',
      'Improving app performance and fixing bugs',
      'Supporting an ad-funded experience and measuring advertising performance, where applicable and subject to your choices',
      'Complying with legal obligations when required',
    ],
    paragraphs: [
      'We do not sell your personal data. We do not run a data-broker business on the side of your dating life.',
    ],
  }),
  section('Verification Privacy', '✓', {
    intro:
      'Verification is sensitive — especially for closeted and privacy-conscious queer users. We treat it that way.',
    subsections: [
      {
        heading: 'Public verification',
        paragraphs: [
          'If you choose public verification, a badge may appear on your profile after approval. The underlying selfie and liveness media remain private and are not shown to other users.',
        ],
      },
      {
        heading: 'Private / discreet verification',
        paragraphs: [
          'Private verification is for users who want stronger internal trust without a public badge. Verification media is never displayed to other users — ever.',
        ],
      },
      {
        heading: 'Who can access verification media',
        bullets: [
          'Only authorized moderation staff on a need-to-know basis',
          'Access is role-restricted, logged, and used for identity review and fraud prevention',
          'Verification confirms identity review — not behavior, intentions, or future safety',
        ],
      },
    ],
    paragraphs: [
      'See Data Retention below for how long verification media is stored.',
    ],
  }),
  section('Location Privacy', '📍', {
    intro:
      'Location powers nearby discovery — but we design it to reduce exposure risk.',
    bullets: [
      'Other users generally see approximate distance, not your exact real-time coordinates',
      'You control visibility features where available (including temporary or limited visibility)',
      'We do not intentionally provide your precise GPS coordinates or profile location to advertising partners for ad targeting',
      'You can adjust device-level location permissions in your phone settings at any time',
    ],
    paragraphs: [
      'No location system is risk-free. Be thoughtful about what you share in chats and profiles, especially if you are not out publicly.',
    ],
  }),
  section('Advertising, Tracking & Your Choices', '📣', {
    intro:
      'Some versions of StrapIn include an ad-supported experience. We use advertising partners to display and measure ads while keeping your safety and privacy choices in view.',
    bullets: [
      'Google Mobile Ads and enabled mediation partners may process device identifiers (including the advertising identifier where you permit it), IP-derived general location, ad views, ad interactions, and limited diagnostic or performance information to serve, measure, and improve ads',
      'On iOS, where required, we ask for App Tracking Transparency permission before allowing access to the advertising identifier for personalized advertising and cross-app advertising measurement',
      'You can decline tracking permission in the system prompt or change that choice later in your device privacy settings. Declining does not prevent use of StrapIn; ads may be contextual, non-personalized, limited, or otherwise less tailored',
      'We use the consent and privacy controls made available by our advertising partners where required by applicable law',
      'We do not intentionally provide your profile fields, private messages, uploaded media, verification media, or precise GPS coordinates to advertising partners for ad targeting',
    ],
    paragraphs: [
      'We do not sell personal information for money. However, privacy laws can define “sale” or “sharing” broadly. Where advertising-related identifier sharing is treated as a sale, share, or targeted advertising activity, you may have a right to opt out; contact privacy@strapinapp.com and use the in-app or device controls described above.',
    ],
  }),
  section('Service Providers & Third Parties', '🤝', {
    intro:
      'We use carefully selected service providers to operate StrapIn. They may process only the information needed to provide their services, subject to their own terms and privacy notices.',
    bullets: [
      'Authentication providers, including Sign in with Apple and Google Sign-In, when you choose those sign-in methods',
      'Cloud, hosting, storage, and communications providers that operate the app, deliver media, and maintain reliable service',
      'Firebase Cloud Messaging and Apple Push Notification service to deliver notifications using device and push-registration identifiers',
      'Mapping and places providers when you use location or map-based features',
      'Google Mobile Ads and enabled ad-mediation partners for advertising, measurement, consent, and fraud-prevention functions as described above',
      'Payment platforms, including Apple App Store or Google Play, when you purchase subscriptions or in-app features; we receive transaction-status information needed to provide your purchase, not your full payment-card details',
    ],
  }),
  section('Messaging & Media Privacy', '💬', {
    intro:
      'Chats, photos, and albums are meant to stay between the people involved.',
    bullets: [
      'Messages and media are stored to deliver the service and support safety investigations when needed',
      'StrapIn actively discourages and restricts screenshots and screen recordings in-app',
      'No technical solution prevents all capture on every device — please use judgment and report misuse',
      'Sharing private content without consent violates our Terms and may result in permanent removal',
    ],
  }),
  section('Safety & Moderation Data', '🛡️', {
    intro:
      'When you report someone, we collect the information needed to review the case fairly and protect users.',
    bullets: [
      'Report category, optional description, and related profile or chat context',
      'Moderation decisions, warnings, suspensions, and bans',
      'Fraud signals and impersonation investigations',
      'Records related to harassment, scams, blackmail, and outing threats',
      'Security-event links between relevant accounts, reports, conversations, and private Safe Meet actions',
    ],
    paragraphs: [
      'We may retain certain safety records after account deletion where necessary to prevent repeat harm, comply with law, or defend against legal claims — always limited to what is reasonably necessary.',
      'IP addresses and similar network identifiers are supporting security signals, not proof of identity or exact location. We do not expose them to other users, and we do not make enforcement decisions from an IP match alone.',
    ],
  }),
  section('Network & Safety Evidence', '🔎', {
    intro:
      'To investigate scams, fraud, account takeovers, repeat abuse, and serious offline incidents, StrapIn keeps a limited record of important account and safety events.',
    subsections: [
      {
        heading: 'Events we may record',
        bullets: [
          'Account registration, sign-in, and authenticated network activity',
          'Message-send events, without creating an extra plaintext copy of end-to-end encrypted message content',
          'Safety reports and the accounts, conversations, or selected evidence connected to them',
          'Private Safe Meet creation, check-in, help, resolution, and cancellation events',
        ],
      },
      {
        heading: 'Technical information attached to an event',
        bullets: [
          'Server-observed IP address and UTC timestamp',
          'App platform, version, request identifier, and pseudonymous device or session fingerprints where available',
          'Identifiers for the relevant account, other account, conversation, report, message, or Safe Meet plan',
        ],
      },
      {
        heading: 'How we protect and use it',
        bullets: [
          'Full IP evidence is encrypted and is not displayed to users or ordinary support staff',
          'Keyed fingerprints can help identify related abusive accounts without exposing the underlying address',
          'Shared Wi-Fi, mobile carriers, workplaces, VPNs, and households can share an IP, so an IP match is never treated as proof of identity or wrongdoing by itself',
          'Sensitive evidence access and exports are role-restricted and audited',
        ],
      },
    ],
  }),
  section('Data Retention', '⏳', {
    intro: 'We keep data only as long as needed for the purposes described in this policy:',
    bullets: [
      'Approved verification media: up to 365 days, then deleted or anonymized unless a longer hold applies',
      'Rejected verification media: up to 180 days',
      'Account and network security-event logs: generally 180 days, unless a shorter deletion rule or a properly scoped safety or legal hold applies',
      'Fraud, abuse, or legal holds: longer retention where reasonably necessary for investigation or compliance',
      'Active account data: retained while your account is active',
      'Deleted accounts: personal data deleted or anonymized per our deletion workflow, subject to safety and legal exceptions',
      'Backups: may persist for a limited period in secure backup systems before rolling off',
    ],
  }),
  section('Account Deletion', '🗑️', {
    intro:
      'You can delete your StrapIn account through in-app settings or our account deletion page.',
    bullets: [
      'Profile, discovery presence, and most personal data are removed from active systems',
      'Messages and media may be deleted from your side; recipients may still have copies in their chat history until they delete their account or content',
      'Some logs, safety records, or verification holds may be retained where legally required or reasonably necessary to prevent fraud and repeat abuse',
    ],
    paragraphs: [
      'For deletion help, visit our Account Deletion page or email privacy@strapinapp.com.',
    ],
  }),
  section('Security Measures', '🔐', {
    intro:
      'We use industry-appropriate safeguards to protect your information. No system is perfectly secure — and we will not claim otherwise.',
    bullets: [
      'Encryption in transit (TLS) and encryption at rest where appropriate',
      'Restricted employee access on a need-to-know basis',
      'Role-based permissions for moderation and verification review',
      'Encrypted storage of full IP evidence, pseudonymous fingerprints for fraud matching, and audit logs for sensitive evidence access',
      'Secure cloud infrastructure and monitoring for suspicious activity',
      'Authentication protections for account access',
    ],
    paragraphs: [
      'You play a role too: use a strong device passcode, do not share login credentials, and report suspicious access immediately.',
    ],
  }),
  section('International Privacy Rights', '🌍', {
    intro:
      'StrapIn serves users globally. Depending on where you live, you may have additional rights:',
    subsections: [
      {
        heading: 'European Economic Area & UK (GDPR / UK GDPR)',
        bullets: [
          'Right to access, correct, delete, or restrict processing of your personal data',
          'Right to object to certain processing and to data portability where applicable',
          'Right to lodge a complaint with your local supervisory authority',
          'Legal bases include contract performance, legitimate interests (safety, fraud prevention), and consent where required',
        ],
      },
      {
        heading: 'India (Digital Personal Data Protection Act)',
        bullets: [
          'Rights to access, correction, erasure, and grievance redressal as applicable',
          'Processing for lawful purposes with appropriate safeguards for sensitive personal data',
        ],
      },
      {
        heading: 'California & certain US states (CCPA / CPRA baseline)',
        bullets: [
          'Right to know categories of personal information collected and purposes',
          'Right to delete personal information, subject to legal exceptions',
          'We do not sell personal information for money. We describe advertising-related sharing and available opt-out choices above because some privacy laws define “sale” or “sharing” broadly',
          'Right to non-discrimination for exercising privacy rights',
        ],
      },
    ],
    paragraphs: [
      'To exercise your rights, email privacy@strapinapp.com. We may need to verify your identity before processing requests.',
    ],
  }),
  section('Legal Requests & Law Enforcement', '🏛️', {
    intro:
      'We resist overbroad surveillance culture. We disclose user information to authorities only when legally required and properly scoped.',
    bullets: [
      'Valid legal process reviewed by our team',
      'Narrow requests where possible — we push back on overreach',
      'User notice when permitted and safe',
      'Expedited handling for imminent harm and mandatory child-safety reporting where required',
      'Preservation requests prevent scoped records from expiring but do not by themselves authorize disclosure',
      'Sensitive evidence exports require restricted access, independent approval, and an audit record',
    ],
    paragraphs: [
      'Where legally required and properly scoped, records available for disclosure may include account details, timestamps, IP and device security records, report context, Safe Meet events, and communications covered by the applicable legal authority.',
      'An FIR, report, or preservation request does not automatically give unrestricted access to user data. We verify the requesting authority, jurisdiction, legal basis, requested accounts, date range, and categories of information before disclosure.',
    ],
  }),
  section('Policy Changes', '📅', {
    intro:
      'We may update this Privacy Policy as StrapIn evolves, laws change, or we add new features.',
    paragraphs: [
      'When we make material changes, we will provide notice in the app or on this page with an updated “Last updated” date.',
      'Continued use after changes take effect means you accept the updated policy, subject to your rights under applicable law.',
    ],
  }),
];

export const PRIVACY_LAST_UPDATED = 'September 15, 2026';
